import { Button, Chip, LinearProgress, MenuItem, Table, TableBody, TableCell, TableHead, TableRow, TextField } from '@material-ui/core';
import { createStyles, withStyles, WithStyles } from '@material-ui/core/styles';
import WarningIcon from '@material-ui/icons/Warning';
import * as React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import * as XLSX from 'xlsx';
import * as CreditsActions from '../../redux/credits/credits.actions';
import * as ModalsActions from '../../redux/modals/modals.actions';
import * as SettingsActions from '../../redux/settings/settings.actions';
import { IState } from '../../redux/store';
import IMarketplaceInDTO from '../../repository/jago/model/input/IMarketplaceInDTO';
import { ISubscriptionInDTO } from '../../repository/jago/model/input/ISubscriptionInDTO';
import { GREEN, GREEN_MAIN, ORANGE, RED } from '../../style/StyleConsts';
import translations from '../../translations/i18next';
import { applyPricelistPlan, describeError } from '../../utils/pricelist/applier';
import { fetchAllMarketplaceSubscriptions, pricelistApplyApi } from '../../utils/pricelist/api';
import {
  buildTemplateWorkbook,
  defaultCompanyFor,
  distinctCompanies,
  findPricelistSheetNames,
  parsePricelist,
} from '../../utils/pricelist/parser';
import { planPricelist } from '../../utils/pricelist/planner';
import {
  IFieldChange,
  IPricelistApplyResult,
  IPricelistDiffEntry,
  IPricelistParseResult,
  IPricelistPlan,
  PricelistDiffStatus,
  PRICELIST_DIFF_STATUSES,
} from '../../utils/pricelist/types';
import Utils from '../../utils/Utils';
import { ModalTypes } from '../Modals/ModalTypes';
import ReduxLanguage from '../ReduxLanguage';

const MAX_RENDERED_ROWS = 500;

const STATUS_COLORS: Record<PricelistDiffStatus, string> = {
  NEW: GREEN_MAIN,
  CHANGED: ORANGE,
  READONLY_CHANGED: '#8D6E63',
  UNCHANGED: '#9E9E9E',
  ERROR: RED,
  SKIPPED: '#5C6BC0',
};

const FIELD_LABEL_KEYS: Record<string, string> = {
  fixedPrice: 'forms.subscriptionFixedPrice',
  burnRate: 'forms.subscriptionBurnRate',
  visible: 'forms.subscriptionVisible',
  requestToQuote: 'forms.requestToQuote',
  canBeDeactivated: 'forms.subscriptionCanBeDeactivated',
  provisioningType: 'forms.provisioningType',
};

const styles = createStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'white',
    padding: 20,
    height: '100%',
    overflowY: 'auto',
    boxSizing: 'border-box',
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 16,
  },
  chips: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 16,
  },
  tableWrapper: {
    overflowX: 'auto',
    marginBottom: 16,
  },
  errorText: {
    color: RED,
    margin: 0,
    fontSize: 12,
  },
  warningText: {
    color: '#8D6E63',
    margin: 0,
    fontSize: 12,
  },
  muted: {
    color: '#757575',
    fontSize: 13,
    margin: '4px 0',
  },
  link: {
    cursor: 'pointer',
    textDecoration: 'underline',
    color: '#1B97C2',
    fontSize: 13,
  },
});

export interface IPricelistImportViewProps extends WithStyles<typeof styles> {
  marketplace: IMarketplaceInDTO;
}

export interface IPricelistImportViewState {
  workbook?: XLSX.WorkBook;
  fileName?: string;
  sheetNames: string[];
  sheetName?: string;
  parseResult?: IPricelistParseResult;
  companies: string[];
  company?: string;
  existing?: ISubscriptionInDTO[];
  plan?: IPricelistPlan;
  filter: PricelistDiffStatus | null;
  fileError?: string;
  progress?: { done: number; total: number };
  results?: IPricelistApplyResult[];
}

type IReduxProps = ConnectedProps<typeof connector>;
export type ComponentProps = IPricelistImportViewProps & IReduxProps;

class PricelistImportView extends React.Component<ComponentProps, IPricelistImportViewState> {
  private mounted = false;

  constructor(props: ComponentProps) {
    super(props);
    this.state = { sheetNames: [], companies: [], filter: null };
  }

  public componentDidMount() {
    this.mounted = true;
  }

  public componentWillUnmount() {
    this.mounted = false;
  }

  private showError(message: string) {
    const { dispatch } = this.props;
    dispatch<any>(ModalsActions.showModal('ERROR_PRICELIST_IMPORT', {
      modalType: ModalTypes.ERROR_MODAL,
      modalProps: {
        titleMessageKey: 'errors.error',
        errorMessageKey: 'credits.pricelistError',
        errorMessageProps: { message },
      },
    }));
  }

  private async loadExistingSubscriptions(): Promise<ISubscriptionInDTO[]> {
    const { marketplace } = this.props;
    return fetchAllMarketplaceSubscriptions(marketplace.id);
  }

  public async onFileSelected(event: React.ChangeEvent<HTMLInputElement>) {
    const { dispatch } = this.props;
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) {
      return;
    }
    try {
      dispatch<any>(SettingsActions.setSpinnerVisible(true));
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      const sheetNames = workbook.SheetNames;
      const sheetName = findPricelistSheetNames(workbook)[0] || sheetNames[0];
      const existing = await this.loadExistingSubscriptions();
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      if (!this.mounted) return;
      this.setState(
        { workbook, fileName: file.name, sheetNames, existing, fileError: undefined, results: undefined, progress: undefined },
        () => this.selectSheet(sheetName),
      );
    } catch (error) {
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      this.showError(describeError(error));
    }
  }

  public selectSheet(sheetName: string) {
    const { workbook } = this.state;
    const { marketplace } = this.props;
    if (!workbook) return;
    try {
      const parseResult = parsePricelist(workbook, sheetName);
      const companies = distinctCompanies(parseResult);
      const company = defaultCompanyFor(companies, marketplace.name) || (companies.length === 1 ? companies[0] : undefined);
      this.setState({ sheetName, parseResult, companies, company, fileError: undefined, filter: null }, () => this.recomputePlan());
    } catch (error) {
      this.setState({ sheetName, parseResult: undefined, companies: [], company: undefined, plan: undefined, fileError: describeError(error) });
    }
  }

  public selectCompany(company: string) {
    this.setState({ company, filter: null }, () => this.recomputePlan());
  }

  private recomputePlan() {
    const { parseResult, company, existing } = this.state;
    if (!parseResult || !company || !existing) {
      this.setState({ plan: undefined });
      return;
    }
    const rows = parseResult.rows.filter(r => r.company === company);
    this.setState({ plan: planPricelist(rows, existing) });
  }

  private countErrors(): number {
    const { plan, parseResult, company } = this.state;
    const parseErrors = parseResult ? parseResult.errors.filter(e => e.company === company).length : 0;
    return (plan ? plan.counts.ERROR : 0) + parseErrors;
  }

  public onApplyRequest() {
    const { dispatch } = this.props;
    const { plan } = this.state;
    if (!plan) return;
    const created = plan.counts.NEW;
    const updated = plan.counts.CHANGED;
    const errors = this.countErrors();
    dispatch<any>(ModalsActions.showModal('PRICELIST_APPLY_CONFIRM_MODAL', {
      modalType: ModalTypes.CONFIRM_MODAL,
      modalProps: {
        icon: <WarningIcon style={{ color: 'orange', fontSize: 50 }} />,
        titleMessageKey: 'credits.pricelistApplyTitle',
        successMessageKey: errors > 0 ? 'credits.pricelistApplyConfirmWithErrors' : 'credits.pricelistApplyConfirm',
        successMessageProps: { created, updated, errors },
        confirmMessageKey: 'forms.confirm',
        cancelMessageKey: 'forms.cancel',
        onConfirm: () => this.onApplyConfirm(),
        onCancel: () => dispatch<any>(ModalsActions.hideModal('PRICELIST_APPLY_CONFIRM_MODAL')),
      },
    }));
  }

  public async onApplyConfirm() {
    const { dispatch, marketplace } = this.props;
    const { plan } = this.state;
    dispatch<any>(ModalsActions.hideModal('PRICELIST_APPLY_CONFIRM_MODAL'));
    if (!plan) return;
    this.setState({ progress: { done: 0, total: plan.counts.NEW + plan.counts.CHANGED }, results: undefined });
    try {
      const results = await applyPricelistPlan(marketplace.id, plan.entries, pricelistApplyApi, (done, total) => {
        if (this.mounted) this.setState({ progress: { done, total } });
      });
      if (this.mounted) this.setState({ results });
    } catch (error) {
      if (this.mounted) this.setState({ progress: undefined });
      this.showError(describeError(error));
      return;
    }
    try {
      await dispatch<any>(CreditsActions.fetchMarketplaceSubscriptions(marketplace.id));
      const existing = await this.loadExistingSubscriptions();
      if (this.mounted) this.setState({ existing, progress: undefined }, () => this.recomputePlan());
    } catch (error) {
      if (this.mounted) this.setState({ progress: undefined });
      this.showError(describeError(error));
    }
  }

  public onDownloadTemplate() {
    XLSX.writeFile(buildTemplateWorkbook(), 'pricelist-template.xlsx');
  }

  private formatValue(change: IFieldChange, value: IFieldChange['oldValue']): string {
    if (value === undefined || value === null || value === '') return '-';
    if (change.field === 'fixedPrice' || change.field === 'burnRate') return String(Utils.formatCreditsIn(Number(value)));
    if (typeof value === 'boolean') return value ? '1' : '0';
    return String(value);
  }

  private renderChange(change: IFieldChange) {
    const labelKey = FIELD_LABEL_KEYS[change.field];
    const label = labelKey ? translations.t(labelKey) : change.field;
    return (
      <div key={change.field} style={{ fontSize: 12 }}>
        <b>{label}</b>: {this.formatValue(change, change.oldValue)} {'→'} {this.formatValue(change, change.newValue)}
      </div>
    );
  }

  private renderEntry(entry: IPricelistDiffEntry, index: number) {
    const { row } = entry;
    const { classes } = this.props;
    return (
      <TableRow key={`${entry.key}-${row.rowNumber}-${index}`}>
        <TableCell>{row.rowNumber}</TableCell>
        <TableCell>{row.sku}</TableCell>
        <TableCell>{row.name}</TableCell>
        <TableCell>{row.resource}</TableCell>
        <TableCell>{entry.mainTierLabel || '-'}</TableCell>
        <TableCell>
          <Chip
            size="small"
            label={translations.t(`credits.pricelistStatus_${entry.status}`)}
            style={{ backgroundColor: STATUS_COLORS[entry.status], color: 'white', fontWeight: 'bold' }}
          />
        </TableCell>
        <TableCell>
          {entry.changes.map(c => this.renderChange(c))}
          {entry.errors.map(m => <p key={m} className={classes.errorText}>{m}</p>)}
          {entry.warnings.map(m => <p key={m} className={classes.warningText}>{m}</p>)}
        </TableCell>
      </TableRow>
    );
  }

  private renderPreview() {
    const { classes } = this.props;
    const { plan, filter, parseResult, company } = this.state;
    if (!plan) return null;
    const visible = plan.entries.filter(e => !filter || e.status === filter);
    const rendered = visible.slice(0, MAX_RENDERED_ROWS);
    const parseErrors = parseResult ? parseResult.errors.filter(e => e.company === company) : [];
    return (
      <>
        <div className={classes.chips}>
          <Chip
            label={`${translations.t('credits.pricelistFilterAll')} (${plan.entries.length})`}
            color={filter === null ? 'primary' : 'default'}
            onClick={() => this.setState({ filter: null })}
          />
          {PRICELIST_DIFF_STATUSES.map(status => (
            <Chip
              key={status}
              label={`${translations.t(`credits.pricelistStatus_${status}`)} (${plan.counts[status]})`}
              onClick={() => this.setState({ filter: filter === status ? null : status })}
              style={{
                backgroundColor: filter === status ? STATUS_COLORS[status] : undefined,
                color: filter === status ? 'white' : undefined,
                borderLeft: `4px solid ${STATUS_COLORS[status]}`,
              }}
            />
          ))}
        </div>
        {plan.onlyInBackend.length > 0 ? (
          <p className={classes.muted}>{translations.t('credits.pricelistOnlyInBackend', { count: plan.onlyInBackend.length })}</p>
        ) : null}
        {parseErrors.length > 0 ? (
          <div style={{ marginBottom: 16 }}>
            <b style={{ color: RED }}>{translations.t('credits.pricelistParseErrors', { count: parseErrors.length })}</b>
            {parseErrors.map(e => (
              <p key={e.rowNumber} className={classes.errorText}>
                {translations.t('credits.pricelistRowError', { row: e.rowNumber, sku: e.sku || '-', message: e.messages.join('; ') })}
              </p>
            ))}
          </div>
        ) : null}
        <div className={classes.tableWrapper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><ReduxLanguage languageKey="credits.pricelistColumnRow" /></TableCell>
                <TableCell><ReduxLanguage languageKey="forms.sku" /></TableCell>
                <TableCell><ReduxLanguage languageKey="forms.subscriptionName" /></TableCell>
                <TableCell><ReduxLanguage languageKey="forms.subscriptionResource" /></TableCell>
                <TableCell><ReduxLanguage languageKey="credits.pricelistColumnMainTier" /></TableCell>
                <TableCell><ReduxLanguage languageKey="credits.pricelistColumnStatus" /></TableCell>
                <TableCell><ReduxLanguage languageKey="credits.pricelistColumnDetails" /></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rendered.map((entry, index) => this.renderEntry(entry, index))}
            </TableBody>
          </Table>
          {visible.length === 0 ? <p className={classes.muted}><ReduxLanguage languageKey="credits.pricelistNoRows" /></p> : null}
          {visible.length > MAX_RENDERED_ROWS ? (
            <p className={classes.muted}>{translations.t('credits.pricelistShowingFirst', { count: MAX_RENDERED_ROWS, total: visible.length })}</p>
          ) : null}
        </div>
      </>
    );
  }

  private renderResults() {
    const { classes } = this.props;
    const { results } = this.state;
    if (!results) return null;
    const count = (outcome: IPricelistApplyResult['outcome']) => results.filter(r => r.outcome === outcome).length;
    const failed = results.filter(r => r.outcome === 'FAILED');
    return (
      <div style={{ marginBottom: 16 }}>
        <b style={{ color: failed.length > 0 ? RED : GREEN_MAIN }}>
          {translations.t('credits.pricelistResultSummary', { created: count('CREATED'), updated: count('UPDATED'), failed: failed.length })}
        </b>
        {failed.map((r, i) => (
          <p key={`${r.entry.key}-${i}`} className={classes.errorText}>
            {translations.t('credits.pricelistFailedItem', { sku: r.entry.row.sku, tier: r.entry.mainTierLabel || '-', message: r.message })}
          </p>
        ))}
      </div>
    );
  }

  public render() {
    const { classes } = this.props;
    const { sheetNames, sheetName, companies, company, fileName, plan, fileError, progress } = this.state;
    const isApplying = !!progress && progress.done < progress.total;
    const changesCount = plan ? plan.counts.NEW + plan.counts.CHANGED : 0;
    return (
      <div className={classes.container}>
        <div className={classes.row}>
          <Button variant="contained" component="label" disabled={isApplying} style={{ backgroundColor: GREEN, color: 'white', fontWeight: 'bold' }}>
            <ReduxLanguage languageKey={fileName ? 'credits.pricelistChooseAnotherFile' : 'credits.pricelistChooseFile'} />
            <input hidden type="file" accept=".xlsx,.xls,.csv" onChange={e => this.onFileSelected(e)} />
          </Button>
          {fileName ? <span>{fileName}</span> : null}
          <span className={classes.link} onClick={() => this.onDownloadTemplate()}>
            <ReduxLanguage languageKey="credits.pricelistDownloadTemplate" />
          </span>
        </div>
        {sheetNames.length > 0 ? (
          <div className={classes.row}>
            <TextField
              select
              variant="outlined"
              size="small"
              style={{ minWidth: 220 }}
              label={translations.t('credits.pricelistSheet')}
              value={sheetName || ''}
              disabled={isApplying}
              onChange={e => this.selectSheet(e.target.value)}
            >
              {sheetNames.map(name => <MenuItem key={name} value={name}>{name}</MenuItem>)}
            </TextField>
            <TextField
              select
              variant="outlined"
              size="small"
              style={{ minWidth: 220 }}
              label={translations.t('credits.pricelistCompany')}
              value={company || ''}
              disabled={isApplying || companies.length === 0}
              onChange={e => this.selectCompany(e.target.value)}
            >
              {companies.map(name => <MenuItem key={name} value={name}>{name}</MenuItem>)}
            </TextField>
            <span style={{ flexGrow: 1 }} />
            <Button
              variant="contained"
              disabled={changesCount === 0 || isApplying}
              style={{ backgroundColor: GREEN_MAIN, color: 'white', fontWeight: 'bold', opacity: changesCount === 0 || isApplying ? 0.5 : 1 }}
              onClick={() => this.onApplyRequest()}
            >
              {translations.t('credits.pricelistApply', { count: changesCount })}
            </Button>
          </div>
        ) : null}
        {fileError ? <p className={classes.errorText}>{fileError}</p> : null}
        {sheetNames.length > 0 && !company && !fileError ? (
          <p className={classes.muted}><ReduxLanguage languageKey="credits.pricelistSelectCompany" /></p>
        ) : null}
        {progress ? (
          <div style={{ marginBottom: 16 }}>
            <p className={classes.muted}>{translations.t('credits.pricelistApplying', { done: progress.done, total: progress.total })}</p>
            <LinearProgress variant="determinate" value={progress.total > 0 ? (progress.done / progress.total) * 100 : 100} />
          </div>
        ) : null}
        {this.renderResults()}
        {this.renderPreview()}
      </div>
    );
  }
}

function mapStateToProps(state: IState) {
  return {
    language: state.settings.language,
  };
}

const connector = connect(mapStateToProps);

export default connector(withStyles(styles)(PricelistImportView as any));
